import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, InputNumber, Button, Card, Row, Col, message, Steps,
  Typography, Alert, Space, Tag, Spin, Upload } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, DeleteOutlined, UploadOutlined,
  StarFilled, StarOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import CabecalhoLaudo from '../components/CabecalhoLaudo';

const { Text } = Typography;
const { Option } = Select;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const fmt = (n, c) => (n === null || n === undefined || !isFinite(Number(n))
  ? '—' : Number(n).toFixed(c === undefined ? 2 : c).replace('.', ','));
const aj = (t) => <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, lineHeight: 1.3 }}>{t}</div>;

const UNI_P = [['kgf', 'kgf/cm²'], ['psi', 'psi'], ['bar', 'bar'], ['mpa', 'MPa']];
const EQUIPAMENTOS = ['Vaso de Pressão', 'Tubulação', 'Tubo', 'Mangueira', 'Caldeira',
  'Autoclave', 'Tanque', 'Outro'];

// mesmo mapa da rota: o critério acompanha o equipamento enquanto ninguém o reescreve
const CRITERIO = {
  'Vaso de Pressão': 'ASME Seção VIII, Divisão 1, UG-99',
  'Autoclave': 'ASME Seção VIII, Divisão 1, UG-99',
  'Tubulação': 'ASME B31.3, item 345.4.2',
  'Tubo': 'ASME B31.3, item 345.4.2',
  'Caldeira': 'ASME Seção I, PG-99',
  'Tanque': 'API 650'
};
const ehCriterioPadrao = (t) => !t || Object.keys(CRITERIO).some((k) => CRITERIO[k] === String(t).trim());

// duracao a partir das horas; atravessar a meia-noite nao vira numero negativo
const minutosEntre = (ini, fim) => {
  if (!ini || !fim) return null;
  const a = String(ini).slice(0, 5).split(':').map(Number);
  const b = String(fim).slice(0, 5).split(':').map(Number);
  if (a.length < 2 || b.length < 2 || a.concat(b).some((x) => !isFinite(x))) return null;
  let dif = (b[0] * 60 + b[1]) - (a[0] * 60 + a[1]);
  if (dif < 0) dif += 24 * 60;
  return dif;
};

const ROTULO_PADRAO = {
  medidor_espessura: 'Medidor de espessura', manometro: 'Manômetro',
  manovacuometro: 'Manovacuômetro', termometro: 'Termômetro',
  bomba: 'Bomba', bomba_hidrostatica: 'Bomba hidrostática', incubadora: 'Incubadora'
};
const nomePadrao = (t) => ROTULO_PADRAO[t] ||
  String(t || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export default function TesteHidrostatico() {
  const nav = useNavigate();
  const { id } = useParams();

  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [step, setStep] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [padroes, setPadroes] = useState([]);
  const [fluidos, setFluidos] = useState([]);
  const [calc, setCalc] = useState(null);
  const [imgs, setImgs] = useState({});
  const [enviando, setEnviando] = useState(false);

  const set = (campo, v) => setD((x) => ({ ...x, [campo]: v }));

  const miniatura = useCallback(async (idx) => {
    try {
      const r = await api.get('/testes-hidrostaticos/' + id + '/fotos/' + idx, { responseType: 'blob' });
      setImgs((p) => ({ ...p, [idx]: URL.createObjectURL(r.data) }));
    } catch (e) { /* miniatura fica vazia */ }
  }, [id]);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/testes-hidrostaticos/' + id);
      const l = r.data;
      setCalc(l.calculo || null);
      delete l.calculo;
      ['pmta_unidade', 'pressao_teste_unidade', 'manometro_fundo_unidade']
        .forEach((k) => { if (!l[k]) l[k] = 'kgf'; });
      if (!l.status) l.status = 'rascunho';
      setD(l);
      (l.fotos || []).forEach((f, i) => miniatura(i));
    } catch { message.error('Erro ao carregar o laudo'); }
  }, [id, miniatura]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/testes-hidrostaticos/apoio/padroes').then((r) => setPadroes(r.data || [])).catch(() => {});
    api.get('/classificacao-nr13/fluidos').then((r) => setFluidos(r.data || [])).catch(() => {});
  }, []);

  const recarregar = useCallback(async () => {
    try {
      const r = await api.get('/testes-hidrostaticos/' + id);
      const l = r.data;
      setCalc(l.calculo || null);
      setD((x) => ({ ...x, codigo: l.codigo, revisao: l.revisao, status: l.status || x.status,
        atualizado_em: l.atualizado_em, fotos: l.fotos }));
      (l.fotos || []).forEach((f, i) => miniatura(i));
    } catch { /* mantem o que esta na tela */ }
  }, [id, miniatura]);

  const subir = async (file) => {
    const fd = new FormData(); fd.append('foto', file);
    setEnviando(true);
    try { await api.post('/testes-hidrostaticos/' + id + '/fotos', fd); await recarregar(); }
    catch (e) { message.error('Erro ao enviar a imagem'); }
    setEnviando(false);
    return false;
  };
  const remover = async (idx) => {
    try {
      await api.delete('/testes-hidrostaticos/' + id + '/fotos/' + idx);
      setImgs({});
      await recarregar();
    } catch (e) { message.error('Erro ao remover'); }
  };
  const definirCapa = async (idx) => {
    try { await api.put('/testes-hidrostaticos/' + id + '/fotos/' + idx + '/principal'); await recarregar(); }
    catch (e) { message.error('Erro ao definir a capa'); }
  };

  const salvar = async (aviso = true) => {
    if (!d) return null;
    setSalvando(true);
    try {
      const corpo = { ...d };
      ['id', 'codigo', 'criado_em', 'atualizado_em', 'disponivel_portal', 'app_uuid',
        'razao_social', 'nome_cliente', 'cnpj', 'art_cliente', 'cnpj_faturamento',
        'os_numero', 'end_atend_logradouro', 'end_atend_numero', 'end_atend_bairro',
        'end_atend_cidade', 'end_atend_estado', 'end_atend_cep', 'fotos', 'calculo']
        .forEach((k) => delete corpo[k]);
      const r = await api.put('/testes-hidrostaticos/' + id, corpo);
      setCalc(r.data.calculo || null);
      await recarregar();
      naoSalvo.limpo();
      if (aviso) message.success('Laudo salvo');
      return r.data.calculo;
    } catch (e) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.erro || e.message));
      return null;
    } finally { setSalvando(false); }
  };

  const gerarPdf = async () => {
    message.loading({ content: 'Gerando o PDF...', key: 'pdf', duration: 0 });
    try {
      await salvar(false);
      const r = await api.get('/testes-hidrostaticos/' + id + '/pdf', { responseType: 'blob' });
      message.destroy('pdf');
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      await recarregar();
    } catch (e) {
      message.destroy('pdf');
      let msg = 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) { /* nao e json */ }
      message.error(msg);
    }
  };

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const rotUni = (u) => (UNI_P.find((x) => x[0] === (u || 'kgf')) || ['', 'kgf/cm²'])[1];
  const pTeste = num(d.pressao_teste);
  const faixaIdeal = pTeste && pTeste > 0
    ? { min: pTeste * 1.5, ideal: pTeste * 2, max: pTeste * 4 } : null;

  const unidade = (campo, span = 2) => (
    <Col span={span}>
      <label>Un.</label>
      <Select style={{ width: '100%' }} value={d[campo] || 'kgf'}
        onChange={(v) => set(campo, v)}
        options={UNI_P.map(([v, r]) => ({ value: v, label: r }))} />
    </Col>
  );

  const simNao = (campo, rotulo, ajuda, span = 8) => (
    <Col span={span}>
      <label>{rotulo}</label>
      <Select style={{ width: '100%' }} allowClear placeholder="—"
        value={d[campo] === true ? true : d[campo] === false ? false : undefined}
        onChange={(v) => set(campo, v === undefined ? null : v)}
        options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} />
      {ajuda ? aj(ajuda) : null}
    </Col>
  );

  const etapas = [{ title: 'Identificação' }, { title: 'Equipamento' },
    { title: 'Parâmetros' }, { title: 'Execução' }, { title: 'Fotos' }, { title: 'Resultado' }];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/laudos')}>Voltar</Button>
        <b>Teste Hidrostático</b>
        {d.codigo && <Tag color="blue">{d.codigo}</Tag>}
        {calc && calc.queda_mpa > 0 && <Tag color="red">queda de pressão</Tag>}
      </Space>

      {calc && calc.avisos && calc.avisos.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Pontos de atenção"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>
            {calc.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>} />
      )}

      <Steps current={step} size="small" items={etapas} onChange={setStep} style={{ marginBottom: 16 }} />

      {step === 0 && <CabecalhoLaudo d={d} set={set} />}

      {step === 1 && (
        <Card title="Equipamento ensaiado" size="small">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Tipo de equipamento</label>
              <Select style={{ width: '100%' }} value={d.tipo_equipamento || undefined}
                onChange={(v) => setD((x) => ({ ...x, tipo_equipamento: v,
                  criterio: ehCriterioPadrao(x.criterio) ? (CRITERIO[v] || '') : x.criterio }))}
                options={EQUIPAMENTOS.map((e) => ({ value: e, label: e }))} />
            </Col>
            {d.tipo_equipamento === 'Outro' && (
              <Col span={6}>
                <label>Qual equipamento</label>
                <Input value={d.tipo_equipamento_outro || ''}
                  onChange={(e) => set('tipo_equipamento_outro', e.target.value)} />
              </Col>
            )}
            <Col span={6}>
              <label>TAG</label>
              <Input value={d.tag || ''} onChange={(e) => set('tag', e.target.value)} />
            </Col>
            <Col span={6}>
              <label>Descrição</label>
              <Input value={d.equipamento || ''} onChange={(e) => set('equipamento', e.target.value)} />
            </Col>
            <Col span={6}>
              <label>Modelo</label>
              <Input value={d.modelo || ''} onChange={(e) => set('modelo', e.target.value)} />
            </Col>

            <Col span={5}>
              <label>Nº de série</label>
              <Input value={d.numero_serie || ''} onChange={(e) => set('numero_serie', e.target.value)} />
            </Col>
            <Col span={5}>
              <label>Fabricante</label>
              <Input value={d.fabricante || ''} onChange={(e) => set('fabricante', e.target.value)} />
            </Col>
            <Col span={4}>
              <label>Ano de fabricação</label>
              <InputNumber style={{ width: '100%' }} min={1900} max={2100}
                value={num(d.ano_fabricacao)} onChange={(v) => set('ano_fabricacao', v)} />
            </Col>
            <Col span={6}>
              <label>Local de instalação</label>
              <Input value={d.local_instalacao || ''} onChange={(e) => set('local_instalacao', e.target.value)} />
            </Col>

            <Col span={6}>
              <label>Fluido de operação</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.fluido || undefined} onChange={(v) => set('fluido', v || null)}>
                {fluidos.map((f) => <Option key={f.id} value={f.nome}>{f.nome}</Option>)}
                <Option value="outro">Outro</Option>
              </Select>
            </Col>
            {d.fluido === 'outro' && (
              <Col span={6}>
                <label>Qual fluido</label>
                <Input value={d.fluido_outro || ''} onChange={(e) => set('fluido_outro', e.target.value)} />
              </Col>
            )}
            <Col span={4}>
              <label>PMTA</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pmta)} onChange={(v) => set('pmta', v)} />
            </Col>
            {unidade('pmta_unidade')}
          </Row>
        </Card>
      )}

      {step === 2 && (
        <>
          <Card title="Parâmetros do teste" size="small">
            <Row gutter={[16, 16]}>
              <Col span={4}>
                <label>Pressão de teste</label>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                  value={num(d.pressao_teste)} onChange={(v) => set('pressao_teste', v)} />
                {aj('Definida pelo responsável técnico.')}
              </Col>
              {unidade('pressao_teste_unidade')}
              <Col span={5}>
                <label>Fluido do teste</label>
                <Input value={d.fluido_teste || ''} onChange={(e) => set('fluido_teste', e.target.value)} />
              </Col>
              <Col span={5}>
                <label>Temperatura do fluido</label>
                <Input value={d.temperatura_fluido || ''}
                  onChange={(e) => set('temperatura_fluido', e.target.value)} />
              </Col>
              <Col span={8}>
                <label>Critério de aceitação</label>
                <Input value={d.criterio || ''} onChange={(e) => set('criterio', e.target.value)}
                  placeholder="Ex.: ASME VIII Div. 1, UG-99" />
              </Col>
            </Row>
          </Card>

          <Card title="Instrumentos" size="small" style={{ marginTop: 12 }}>
            {faixaIdeal && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message={'Faixa adequada do manômetro para ' + fmt(pTeste, 2) + ' ' +
                  rotUni(d.pressao_teste_unidade)}
                description={'Entre ' + fmt(faixaIdeal.min, 1) + ' e ' + fmt(faixaIdeal.max, 1) +
                  ', de preferência perto de ' + fmt(faixaIdeal.ideal, 1) + ' ' +
                  rotUni(d.pressao_teste_unidade) + '. Manômetro largo demais não tem resolução ' +
                  'para enxergar queda de pressão.'} />
            )}
            <Row gutter={[16, 16]}>
              <Col span={10}>
                <label>Manômetro padrão</label>
                <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                  value={d.manometro_modelo || undefined}
                  onChange={(v) => {
                    const p = padroes.find((x) => x.identificacao === v);
                    setD((x) => ({ ...x, manometro_modelo: v,
                      manometro_serie: p ? p.certificado : x.manometro_serie,
                      manometro_padrao_tipo: p ? p.tipo : x.manometro_padrao_tipo }));
                  }}>
                  {padroes.map((p) => (
                    <Option key={p.identificacao} value={p.identificacao}>
                      {nomePadrao(p.tipo)} — {p.identificacao}{p.marca ? ' (' + p.marca + ')' : ''}
                    </Option>
                  ))}
                </Select>
                {aj('É o manômetro que mede a pressão do ensaio, e por isso o certificado dele é o que importa.')}
              </Col>
              <Col span={4}>
                <label>Fundo de escala</label>
                <InputNumber style={{ width: '100%' }} min={0} step={1} decimalSeparator=","
                  value={num(d.manometro_fundo_escala)}
                  onChange={(v) => set('manometro_fundo_escala', v)} />
              </Col>
              {unidade('manometro_fundo_unidade')}
              <Col span={4}>
                <label>Marca da bomba</label>
                <Input value={d.bomba_marca || ''} onChange={(e) => set('bomba_marca', e.target.value)} />
              </Col>
              <Col span={4}>
                <label>Modelo da bomba</label>
                <Input value={d.bomba_modelo || ''} onChange={(e) => set('bomba_modelo', e.target.value)} />
              </Col>
              <Col span={4}>
                <label>Nº de série da bomba</label>
                <Input value={d.bomba_serie || ''} onChange={(e) => set('bomba_serie', e.target.value)} />
                {aj('A bomba não é instrumento de medição e não entra no cadastro de padrões.')}
              </Col>
            </Row>
          </Card>

          <Card title="Preparação e segurança" size="small" style={{ marginTop: 12 }}>
            <Row gutter={[16, 16]}>
              {simNao('equipamento_purgado', 'Equipamento purgado de ar?',
                'Ar preso dentro do equipamento armazena energia: é o que transforma um vazamento em acidente.')}
              {simNao('dispositivos_removidos', 'Dispositivos de segurança removidos ou bloqueados?',
                'Com a válvula de segurança montada, ela abre antes de o teste chegar na pressão.')}
              {simNao('area_isolada', 'Área isolada durante o ensaio?')}
              <Col span={24}>
                <label>Observações da preparação</label>
                <Input.TextArea rows={2} value={d.observacoes_preparacao || ''}
                  onChange={(e) => set('observacoes_preparacao', e.target.value)} />
              </Col>
            </Row>
          </Card>
        </>
      )}

      {step === 3 && (
        <Card title="Execução do teste" size="small"
          extra={<Button type="primary" onClick={() => salvar()}>Salvar e conferir</Button>}>
          <Row gutter={[16, 16]}>
            <Col span={4}>
              <label>Hora de início</label>
              <Input type="time" value={(d.hora_inicio || '').slice(0, 5)}
                onChange={(e) => setD((x) => {
                  const v = e.target.value || null;
                  const dur = minutosEntre(v, x.hora_fim);
                  return { ...x, hora_inicio: v, duracao_min: dur === null ? x.duracao_min : dur };
                })} />
            </Col>
            <Col span={4}>
              <label>Hora de término</label>
              <Input type="time" value={(d.hora_fim || '').slice(0, 5)}
                onChange={(e) => setD((x) => {
                  const v = e.target.value || null;
                  const dur = minutosEntre(x.hora_inicio, v);
                  return { ...x, hora_fim: v, duracao_min: dur === null ? x.duracao_min : dur };
                })} />
            </Col>
            <Col span={4}>
              <label>Duração (min)</label>
              <InputNumber style={{ width: '100%' }} min={0} step={5} decimalSeparator=","
                value={num(d.duracao_min)} onChange={(v) => set('duracao_min', v)} />
              {aj('Calculada pelas horas, mas pode editar.')}
            </Col>
            <Col span={5}>
              <label>Pressão no início da espera</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pressao_inicial)} onChange={(v) => set('pressao_inicial', v)} />
            </Col>
            <Col span={5}>
              <label>Pressão no fim da espera</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pressao_final)} onChange={(v) => set('pressao_final', v)} />
              {aj('A diferença entre as duas é o que aprova ou reprova o teste.')}
            </Col>

            {calc && calc.queda_mpa !== null && calc.queda_mpa !== undefined && (
              <Col span={24}>
                <Alert type={calc.queda_mpa > 0 ? 'error' : 'success'} showIcon
                  message={calc.queda_mpa > 0
                    ? 'Houve queda de pressão de ' + fmt(calc.queda_pct, 1) + '%'
                    : 'A pressão se manteve durante a espera'} />
              </Col>
            )}

            {calc && calc.queda_mpa > 0 && (
              <Col span={24}>
                <label>Justificativa da variação de pressão</label>
                <Input.TextArea rows={2} value={d.justificativa_variacao || ''}
                  onChange={(e) => set('justificativa_variacao', e.target.value)}
                  placeholder="Ex.: variação compatível com a queda de temperatura da água durante a espera." />
                {aj('Não existe tolerância de queda em norma. Se a variação for aceita, o laudo precisa dizer por quê.')}
              </Col>
            )}
            <Col span={12}>
              <label>Vazamentos observados</label>
              <Input.TextArea rows={3} value={d.vazamentos || ''}
                onChange={(e) => set('vazamentos', e.target.value)}
                placeholder="Deixe vazio se não houve." />
            </Col>
            <Col span={12}>
              <label>Deformações observadas</label>
              <Input.TextArea rows={3} value={d.deformacoes || ''}
                onChange={(e) => set('deformacoes', e.target.value)}
                placeholder="Deixe vazio se não houve." />
            </Col>
          </Row>
        </Card>
      )}

      {step === 4 && (
        <Card title="Registro fotográfico" size="small"
          extra={<Upload beforeUpload={subir} showUploadList={false} accept="image/*" multiple>
            <Button icon={<UploadOutlined />} loading={enviando}>Adicionar foto</Button>
          </Upload>}>
          <Row gutter={[12, 12]}>
            {(d.fotos || []).map((f, i) => (
              <Col span={6} key={i}>
                <Card size="small" cover={imgs[i]
                  ? <img alt={f.nome} src={imgs[i]} style={{ height: 150, objectFit: 'cover' }} />
                  : <div style={{ height: 150, background: '#fafafa' }} />}
                  actions={[
                    <Button type="text" size="small" onClick={() => definirCapa(i)}
                      icon={f.principal ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} />,
                    <Button type="text" size="small" danger icon={<DeleteOutlined />}
                      onClick={() => remover(i)} />
                  ]}>
                  <Input size="small" placeholder="Legenda" defaultValue={f.legenda || ''}
                    onBlur={(e) => {
                      const novas = (d.fotos || []).slice();
                      novas[i] = { ...novas[i], legenda: e.target.value };
                      set('fotos', novas);
                    }} />
                </Card>
              </Col>
            ))}
          </Row>
          {(d.fotos || []).length === 0 && <Text type="secondary">Nenhuma foto.</Text>}
        </Card>
      )}

      {step === 5 && (
        <Card title="Resultado e conclusão" size="small"
          extra={<Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>}>
          {calc && calc.resultado_sugerido && (
            <Alert style={{ marginBottom: 16 }}
              type={calc.resultado_sugerido === 'reprovado' ? 'error' : 'success'} showIcon
              message={'Resultado sugerido pelo registro: ' + calc.resultado_sugerido} />
          )}
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Resultado</label>
              <Select style={{ width: '100%' }} allowClear value={d.resultado || undefined}
                onChange={(v) => set('resultado', v || null)}
                options={[{ value: 'aprovado', label: 'Aprovado' },
                  { value: 'reprovado', label: 'Reprovado' }]} />
            </Col>
            <Col span={6}>
              <label>Status do laudo</label>
              <Select style={{ width: '100%' }} value={d.status || 'rascunho'}
                onChange={(v) => set('status', v)}
                options={[{ value: 'rascunho', label: 'Rascunho' },
                  { value: 'concluido', label: 'Concluído' }]} />
            </Col>
            <Col span={24}>
              <label>Conclusão</label>
              <Input.TextArea rows={5} value={d.conclusao || ''}
                onChange={(e) => set('conclusao', e.target.value)} />
            </Col>
          </Row>
        </Card>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button disabled={step === 0} onClick={() => setStep(step - 1)}>Anterior</Button>
          <Button disabled={step === etapas.length - 1} onClick={() => setStep(step + 1)}>Próxima</Button>
        </Space>
        <Space>
          {step === etapas.length - 1 && (
            <Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>
            Salvar
          </Button>
        </Space>
      </div>
    </div>
  );
}
